import { ChangeEvent, useEffect, useRef, useState } from 'react'

import { useToasts } from '@/hooks/useToasts'

import * as Sentry from '@sentry/react'

import { useCreateAiOperatorFeedbackMutation } from '@/graphql/generated/hooks'
import type { IncidentActionType } from '@/graphql/generated/schemas'
import {
  REASONING_TRAIL_FEEDBACK_COMMENT,
  REASONING_TRAIL_FEEDBACK_THUMB,
  trackEvent,
} from '@/utils/analytics'

interface UseAioFeedbackIProps {
  incidentId: string
  /** Thumb state hydrated from `aiOperatorLatestFeedback` (null = neutral). */
  initialIsPositive?: boolean | null
  /** Called after a successful write so the host can refetch the latest state. */
  onSubmitted?: () => void | Promise<unknown>
  /** Trail outcome (RESOLVED/FLAG), attached to feedback analytics events. */
  outcomeKind?: IncidentActionType
  /** Localised error-toast message. */
  errorMessage?: string
}

interface UseAioFeedbackReturnIProps {
  selected: boolean | null
  comment: string
  onCommentChange: (e: ChangeEvent<HTMLTextAreaElement>) => void
  onThumbClick: (isPositive: boolean) => void
  onSubmitComment: () => void
  isSubmitting: boolean
  /** True once a comment has been submitted successfully (drives the confirmation). */
  hasRecordedFeedback: boolean
}

/**
 * Owns the AI Operator feedback interaction. Every write calls the unchanged
 * `createAiOperatorFeedback` mutation (always a new row); a thumb click and a
 * comment submit are separate inserts. After each success the host refetches so
 * the thumb re-hydrates from `aiOperatorLatestFeedback`. The comment is never
 * hydrated from the server — previously-submitted text is never redisplayed.
 */
export const useAioFeedback = ({
  incidentId,
  initialIsPositive = null,
  onSubmitted,
  outcomeKind,
  errorMessage,
}: UseAioFeedbackIProps): UseAioFeedbackReturnIProps => {
  // Tri-state thumb: true = up, false = down, null = neutral.
  const [isPositiveSelection, setIsPositiveSelection] = useState<
    boolean | null
  >(initialIsPositive ?? null)
  const [comment, setComment] = useState('')
  const [hasRecordedFeedback, setHasRecordedFeedback] = useState(false)
  // Loading UI (the Submit button spinner) reflects only the comment submit —
  // thumb clicks write in the background and never show a loading state, even
  // though they share the same mutation.
  const [isSubmittingComment, setIsSubmittingComment] = useState(false)
  const { showError } = useToasts()
  const [createAiOperatorFeedback] = useCreateAiOperatorFeedbackMutation()

  // Always reflects the latest incidentId so async handlers can detect a
  // mid-flight incident switch without needing to close over stale prop values.
  const incidentIdRef = useRef(incidentId)
  useEffect(() => {
    incidentIdRef.current = incidentId
  }, [incidentId])

  // Re-hydrate the thumb from server truth whenever the refetched value
  // changes. Never touches the comment.
  useEffect(() => {
    setIsPositiveSelection(initialIsPositive ?? null)
  }, [initialIsPositive])

  // Reset incident-scoped UI state when the incident changes so that a draft
  // comment or prior confirmation from the previous incident cannot carry over
  // and be submitted against the new incident.
  useEffect(() => {
    setComment('')
    setHasRecordedFeedback(false)
    setIsSubmittingComment(false)
  }, [incidentId])

  const submit = async (
    isPositive: boolean,
    message?: string
  ): Promise<boolean> => {
    try {
      await createAiOperatorFeedback({
        variables: { input: { incidentId, isPositive, message } },
      })
    } catch (error) {
      Sentry.captureException(error)
      showError(errorMessage)
      return false
    }

    // The row was inserted. Re-hydrate from server truth, but a refetch failure
    // must not fail the submit (the write succeeded and the optimistic state
    // already reflects it) — await it so a rejection is caught here rather than
    // surfacing as an unhandled promise rejection, and log it for visibility.
    try {
      await onSubmitted?.()
    } catch (error) {
      Sentry.captureException(error)
    }
    return true
  }

  const onThumbClick = async (isPositive: boolean): Promise<void> => {
    trackEvent(REASONING_TRAIL_FEEDBACK_THUMB, {
      incident_id: incidentId,
      is_positive: isPositive,
      outcome_kind: outcomeKind,
    })
    // Capture the incident at call time so we can detect a switch mid-flight.
    const callerIncidentId = incidentId
    // A new interaction supersedes any prior "recorded" confirmation.
    setHasRecordedFeedback(false)
    // Optimistically reflect the click; the refetch confirms it. On failure,
    // revert to the last server-confirmed value so the UI never claims a thumb
    // the server didn't record.
    setIsPositiveSelection(isPositive)
    const didSucceed = await submit(isPositive)
    // Discard all post-await state updates if the incident changed mid-flight
    // to avoid corrupting the new incident's UI state.
    if (incidentIdRef.current !== callerIncidentId) return
    if (!didSucceed) {
      setIsPositiveSelection(initialIsPositive ?? null)
    }
  }

  const onSubmitComment = async (): Promise<void> => {
    const trimmed = comment.trim()
    // `isPositive` is required, so a thumb must be selected; empty comments are
    // also blocked (the presentational component disables submit in both cases).
    if (isPositiveSelection == null || trimmed.length === 0) {
      return
    }
    trackEvent(REASONING_TRAIL_FEEDBACK_COMMENT, {
      incident_id: incidentId,
      is_positive: isPositiveSelection,
      has_comment: true,
      outcome_kind: outcomeKind,
    })
    // Capture the incident at call time so we can detect a switch mid-flight.
    const callerIncidentId = incidentId
    setIsSubmittingComment(true)
    const didSucceed = await submit(isPositiveSelection, trimmed)
    // Discard all post-await state updates if the incident changed mid-flight.
    if (incidentIdRef.current !== callerIncidentId) return
    setIsSubmittingComment(false)
    if (didSucceed) {
      setComment('')
      // Confirmation renders only after the submit button records a comment.
      setHasRecordedFeedback(true)
    }
  }

  const onCommentChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    setComment(e.target.value)
    // Composing new feedback clears the prior confirmation.
    setHasRecordedFeedback(false)
  }

  return {
    selected: isPositiveSelection,
    comment,
    onCommentChange,
    onThumbClick,
    onSubmitComment,
    isSubmitting: isSubmittingComment,
    hasRecordedFeedback,
  }
}
